import { useMemo, useState, useEffect } from 'react'
import NameValueTable from '../ui-components/NameValueTable'
import InPlaceNameValueAccordion from '../ui-components/InPlaceNameValueAccordion'
import { useMetaDataContext } from '../../state-management/contexts/MetaDataContext'
import {
  convertEpochToDate,
  convertEpochToDateWithOrdinalDay,
  convertUnitAndValue,
  fixedDigitsAndFormatNumberWithCurrency,
  roundOffAndFormatNumberWithCurrency
} from '../../utils/utils'
import TextAndInfo from '../ui-components/TextAndInfo'
import { convertSlabsToNameValueData } from '../../utils/mutualFundDetail'
import { UNITS } from '../../types/misc.types'
import { FundInformationContent } from '../BottomSheetComponents/FundInformationContent'
import VaulDrawer from '../BottomSheetComponents/VaulDrawer'
import { MutualFundDetailSections, MutualFundDetailSubSections } from '../../types/mutual-fund-detail.types'
import { useMutualFundDetailScrollEvent } from '../../hooks/useMutualFundDetailScrollEvent'
import { renderSubSection } from '../../utils/sectionsConfig'
import MFAccordion from '../ui-components/MFAccordion'
import { TaxLiability } from '../sub-sections/taxes/TaxLiability'

const FUND_INFORMATION_SCREEN_NAME = `MF_L2_Fund_Information`

export default function FundInformation({
  sectionKey,
  subsections
}: {
  sectionKey: `${MutualFundDetailSections}`
  subsections: `${MutualFundDetailSubSections}`[]
}) {
  const { data } = useMetaDataContext()
  const [isExitLoadAccordionOpen, setIsExitLoadAccordionOpen] = useState(false)
  const [openBottomSheet, setOpenBottomSheet] = useState(false)
  const fundDetails = useMemo(() => data?.fundDetails, [data])
  const [bottomSheetContentKey, setBottomSheetContentKey] = useState<any>(null)

  const sectionRef = useMutualFundDetailScrollEvent({ sectionKey })

  const renderBottomSheetContent = () => {
    switch (bottomSheetContentKey) {
      case 'fundInformation':
        return (
          <FundInformationContent
            onDismiss={() => setOpenBottomSheet((open) => !open)}
            ScreenName={FUND_INFORMATION_SCREEN_NAME}
          />
        )
      default:
        return null
    }
  }

  const getExitLoadValue = () => {
    let maxValue = Number.NEGATIVE_INFINITY
    let maxDatum = data?.fundDetails?.exitLoad?.slice(-1)?.[0]
    data?.fundDetails?.exitLoad?.forEach((datum) => {
      const currValue = Math.max(datum?.rate?.amount)
      if (currValue > maxValue) {
        maxValue = currValue
        maxDatum = datum
      }
    })
    return `${convertUnitAndValue({
      value: maxDatum?.rate?.amount ?? 0,
      unit: maxDatum?.rate?.unit ?? UNITS.PERCENTAGE
    })}`
  }

  useEffect(() => {
    // trackEvent({
    //   eventName: EventNames.UserClicked,
    //   payload: {
    //     eventId: 'Fund_Information',
    //     responseName: 'Expand',
    //     ScreenName: FUND_INFORMATION_SCREEN_NAME,
    //     fundName: isin,
    //     screenType: 'MF_L2'
    //   }
    // })
  }, [])

  return (
    <div ref={sectionRef} className='bg-white rounded-[12px] pb-[8px] flex flex-col w-full'>
      <div className='flex flex-col w-full px-[20px] pt-[20px] pb-[32px] overflow-x-visible'>
        <div className='pb-3'>
          <TextAndInfo
            text='Fund Information'
            onInfoClick={(event) => {
              event.stopPropagation()
              setBottomSheetContentKey('fundInformation')
              setOpenBottomSheet((open) => !open)
            }}
          />
        </div>

        <NameValueTable
          data={[
            {
              name: `Latest NAV ${data?.fundDetails?.lastSyncDate ? `as of ${convertEpochToDateWithOrdinalDay(data?.fundDetails?.lastSyncDate ?? 0)}` : ''}`,
              value: `${fixedDigitsAndFormatNumberWithCurrency(fundDetails?.navToday ?? 0, 2)}`
            },
            {
              name: 'Total Expense Ratio',
              value:
                convertUnitAndValue({
                  value: fundDetails?.expenseRatio?.amount ?? 0,
                  unit: fundDetails?.expenseRatio?.unit ?? `${UNITS.PERCENTAGE}`
                }) ?? ''
            }
          ]}
          gap={10}
          groupForAccessibility
        />

        <div
          style={{
            width: isExitLoadAccordionOpen ? 'calc(100% + 24px)' : '100%',
            marginLeft: isExitLoadAccordionOpen ? '-12px' : '0px'
          }}
          className='pt-[10px]'
        >
          <InPlaceNameValueAccordion
            name='Exit Load'
            value={getExitLoadValue()}
            accessibilityLabel={`Exit Load, ${getExitLoadValue()}`}
            buttonClassName='min-h-[24px]'
            isExpanded={isExitLoadAccordionOpen}
            onAccordionClick={() =>
              setIsExitLoadAccordionOpen((isExitLoadAccordionOpenPrev) => !isExitLoadAccordionOpenPrev)
            }
          >
            {data?.fundDetails?.exitLoad && (
              <NameValueTable
                data={convertSlabsToNameValueData(
                  data?.fundDetails?.exitLoad ?? [],
                  { FIRST: 'If redeemed within', BETWEEN: 'If redeemed between', LAST: 'If redeemed after' },
                  { FIRST: '', BETWEEN: '', LAST: '' }
                )}
                gap={8}
                groupForAccessibility
              />
            )}
          </InPlaceNameValueAccordion>
        </div>

        <div className='pt-[10px] w-full'>
          <NameValueTable
            data={[
              {
                name: 'Stamp Duty',
                value: `${convertUnitAndValue({ value: data?.stampDuty?.amount ?? 0, unit: data?.stampDuty?.unit ?? `${UNITS.PERCENTAGE}`, formatValue: false })}`
              },
              {
                name: 'Lock-in Period',
                value: fundDetails?.lockInPeriod?.value
                  ? `${convertUnitAndValue({ value: +(fundDetails?.lockInPeriod?.value ?? 0), unit: fundDetails?.lockInPeriod?.period ?? UNITS.MONTH })}`
                  : 'No lock-in'
              },
              {
                name: 'Minimum SIP',
                value: fundDetails?.minSip?.amount
                  ? `${roundOffAndFormatNumberWithCurrency(fundDetails?.minSip?.amount ?? 0)}`
                  : 'No min amount'
              },
              { name: 'Benchmark Index', value: fundDetails?.benchMarkIndex ?? '-' },
              { name: 'Launch Date', value: convertEpochToDate(fundDetails?.launchedOn ?? 0) }
            ]}
            gap={10}
            groupForAccessibility
          />
        </div>
      </div>
      {subsections?.map((subsection, idx) => {
        return <div key={`${subsection}${idx}`}> {renderSubSection({ componentKey: subsection })} </div>
      })}
      <VaulDrawer open={openBottomSheet} setOpen={setOpenBottomSheet}>
        {renderBottomSheetContent()}
        {/* <FundInformationContent onDismiss={() => setOpenBottomSheet((open) => !open)} /> */}
      </VaulDrawer>
    </div>
  )
}
